import { ArrowsArrowUp, ArrowsArrowDown } from "@fanduel/formation-react-icons";
import { fanduel as fd } from "@fanduel/formation-tokens";
import {
  ColumnDef,
  flexRender,
  Row,
  SortingState,
} from "@tanstack/react-table";
import { forwardRef, useEffect } from "react";
import {
  TableVirtuoso,
  TableBodyProps,
  TableProps,
  GridItemProps,
} from "react-virtuoso";
import useTable, { TableVariants } from "@/utils/hooks/useTable";
import { Vtbody, Vtable, Vtr, Vth, VHeader, Vtd } from "../TableStyles";

type TableVirtualizedProps<TData> = {
  data: TData[];
  columns: ColumnDef<TData>[];
  tableVirtuosoStyles: React.CSSProperties;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  onSortChange?: (args: any) => void;
  defaultSortingState?: SortingState;
};

const TableComponent = ({ style, ...props }: TableProps) => (
  <Vtable {...props} style={style} />
);

const TableBodyComponent = forwardRef<HTMLTableSectionElement, TableBodyProps>(
  ({ ...props }, ref) => <Vtbody {...props} ref={ref} />
);

export const TableRowComponent = (props: GridItemProps) => {
  const { "data-index": index } = props;

  return <Vtr data-testid={`tr-${index}`} index={index} {...props} />;
};

export const TableVirtualized = <TData extends object>({
  data,
  columns,
  tableVirtuosoStyles,
  onSortChange,
  defaultSortingState,
}: TableVirtualizedProps<TData>) => {
  const { table, sorting } = useTable({
    data,
    columns,
    variant: TableVariants.VIRTUALIZED,
    defaultSortingState,
  });
  const { rows } = table.getRowModel();

  useEffect(() => {
    if (sorting.length > 0 && onSortChange) {
      onSortChange(sorting);
    }
  }, [sorting, onSortChange]);

  return (
    <TableVirtuoso
      style={{
        tableLayout: "fixed",
        ...tableVirtuosoStyles,
      }}
      totalCount={rows.length}
      initialItemCount={20}
      overscan={10}
      components={{
        Table: TableComponent,
        TableBody: TableBodyComponent,
        TableRow: TableRowComponent,
      }}
      fixedHeaderContent={() =>
        table.getHeaderGroups().map((headerGroup) => (
          <tr key={headerGroup.id}>
            {headerGroup.headers.map((header) => {
              const isSorted = header.column.getIsSorted();
              return (
                <Vth
                  key={header.id}
                  colSpan={header.colSpan}
                  onClick={header.column.getToggleSortingHandler()}
                  isSorted={isSorted}
                >
                  <VHeader>
                    {header.isPlaceholder
                      ? null
                      : flexRender(
                          header.column.columnDef.header,
                          header.getContext()
                        )}
                    {{
                      asc: <ArrowsArrowUp color={fd.colors.coreGreyD4} />,
                      desc: <ArrowsArrowDown color={fd.colors.coreGreyD4} />,
                    }[isSorted as string] ?? ""}
                  </VHeader>
                </Vth>
              );
            })}
          </tr>
        ))
      }
      itemContent={(index) => {
        const row = rows[index] as Row<TData>;
        return row?.getVisibleCells().map((cell) => (
          <Vtd key={cell.id} isSorted={cell.column.getIsSorted()}>
            {flexRender(cell.column.columnDef.cell, cell.getContext())}
          </Vtd>
        ));
      }}
    />
  );
};

TableBodyComponent.displayName = "TableBodyComponent";
